"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, CircleCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/landing/reveal";

/*
 * PricingSection — three plans with a monthly/yearly billing switch
 * (yearly = 20% off). The recommended plan is elevated with the brand
 * bezel treatment. Prices are net, in PLN.
 */

type Billing = "monthly" | "yearly";

type Plan = {
  name: string;
  audience: string;
  monthly: number | null;
  yearly: number | null;
  cta: string;
  href: string;
  features: string[];
  recommended?: boolean;
};

const PLANS: Plan[] = [
  {
    name: "Start",
    audience: "Dla mikrofirm, które chcą raz na zawsze uporządkować dokumenty.",
    monthly: 169,
    yearly: 135,
    cta: "Rozpocznij za darmo",
    href: "/rejestracja",
    features: [
      "Do 3 użytkowników",
      "Rejestr faktur z importem z KSeF",
      "CRM — klienci i historia kontaktów",
      "Oferty z szablonami i eksportem PDF",
      "Miesięczna paczka dla księgowej",
      "Wsparcie e-mail",
    ],
  },
  {
    name: "Biznes",
    audience: "Dla zespołów, które prowadzą wiele projektów jednocześnie.",
    monthly: 399,
    yearly: 319,
    cta: "Rozpocznij za darmo",
    href: "/rejestracja",
    recommended: true,
    features: [
      "Wszystko z planu Start",
      "Do 15 użytkowników i portal pracownika",
      "Projekty, etapy i marża na żywo",
      "Magazyn i listy zakupowe",
      "Automatyzacje: AI z e-maili i Telegram",
      "Rozliczenia i zestawienia końcowe",
      "Raporty finansowe i cashflow",
      "Priorytetowe wsparcie",
    ],
  },
  {
    name: "Enterprise",
    audience: "Dla firm z własnymi procesami, integracjami i wymogami.",
    monthly: null,
    yearly: null,
    cta: "Porozmawiajmy",
    href: "mailto:kontakt@lvsolutions.pl",
    features: [
      "Bez limitu użytkowników",
      "Wdrożenie i migracja danych z opiekunem",
      "Dedykowane automatyzacje i integracje API",
      "Umowa powierzenia (DPA) i wsparcie RODO",
      "SLA 99,5% z gwarancją odtworzenia danych",
      "Szkolenie zespołu",
    ],
  },
];

export function PricingSection() {
  const [billing, setBilling] = useState<Billing>("monthly");

  return (
    <section id="cennik" className="mx-auto max-w-6xl px-6 py-24 lg:py-32">
      <Reveal className="mx-auto max-w-2xl text-center">
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-balance text-foreground sm:text-4xl lg:text-5xl">
          Prosty cennik, który rośnie z firmą
        </h2>
        <p className="mt-5 text-lg leading-relaxed text-pretty text-muted-foreground">
          Bez opłat wdrożeniowych i ukrytych kosztów. Plan zmieniasz albo
          rezygnujesz w dowolnym momencie — Twoje dane zawsze możesz
          wyeksportować.
        </p>

        {/* Billing switch */}
        <div
          role="group"
          aria-label="Okres rozliczenia"
          className="glass mx-auto mt-8 inline-flex items-center gap-1 rounded-full p-1"
        >
          <button
            type="button"
            aria-pressed={billing === "monthly"}
            onClick={() => setBilling("monthly")}
            className={`cursor-pointer rounded-full px-5 py-2 text-sm font-medium transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              billing === "monthly"
                ? "bg-primary text-primary-foreground shadow-soft"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Miesięcznie
          </button>
          <button
            type="button"
            aria-pressed={billing === "yearly"}
            onClick={() => setBilling("yearly")}
            className={`inline-flex cursor-pointer items-center rounded-full px-5 py-2 text-sm font-medium transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              billing === "yearly"
                ? "bg-primary text-primary-foreground shadow-soft"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Rocznie
            <span
              className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                billing === "yearly"
                  ? "bg-white/20 text-primary-foreground"
                  : "bg-primary/10 text-primary"
              }`}
            >
              −20%
            </span>
          </button>
        </div>
      </Reveal>

      <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-3">
        {PLANS.map((plan, i) => {
          const price = billing === "monthly" ? plan.monthly : plan.yearly;
          const card = (
            <article
              className={`relative flex h-full flex-col rounded-2xl p-7 ${
                plan.recommended
                  ? "bg-card"
                  : "glass transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:shadow-elev"
              }`}
            >
              {plan.recommended ? (
                <span className="absolute -top-3.5 left-7 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground shadow-soft">
                  Najczęściej wybierany
                </span>
              ) : null}

              <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
              <p className="mt-2 min-h-10 text-sm leading-relaxed text-muted-foreground">
                {plan.audience}
              </p>

              <div className="mt-6 flex items-baseline gap-2">
                {price != null ? (
                  <>
                    <span className="tabular font-mono text-4xl font-bold text-foreground">
                      {price} zł
                    </span>
                    <span className="text-sm text-muted-foreground">
                      netto / mies.
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-foreground">
                    Indywidualnie
                  </span>
                )}
              </div>
              <p className="mt-1 min-h-5 text-xs text-muted-foreground">
                {price != null
                  ? billing === "yearly"
                    ? "przy rozliczeniu rocznym"
                    : "przy rozliczeniu miesięcznym"
                  : "wycena dopasowana do skali firmy"}
              </p>

              <Button
                render={<Link href={plan.href} />}
                variant={plan.recommended ? "default" : "outline"}
                size="lg"
                className="group mt-6 h-11 w-full gap-2 rounded-full text-sm transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.98]"
              >
                {plan.cta}
                <ArrowRight className="size-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0.5" />
              </Button>

              <ul className="mt-7 space-y-3">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-2.5 text-sm text-foreground"
                  >
                    <CircleCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </article>
          );

          return (
            <Reveal key={plan.name} delay={i * 0.08} className="h-full">
              {plan.recommended ? (
                <div className="bezel h-full shadow-glow">{card}</div>
              ) : (
                card
              )}
            </Reveal>
          );
        })}
      </div>

      <Reveal className="mt-8">
        <p className="text-center text-sm text-muted-foreground">
          Wszystkie ceny netto. Okres próbny trwa 30 dni i obejmuje pełną
          funkcjonalność planu Biznes — bez karty płatniczej.
        </p>
      </Reveal>
    </section>
  );
}
